import { Div, Flex, Main, useResponsiveValue } from "@hopper-ui/components";
import { PageLoader } from "@sg-protect/components";
import { type ReactNode, Suspense, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Outlet } from "react-router";
import { SkipToMain } from "../components/skip-to-main/SkipToMain.tsx";
import { SubscriptionBanners } from "../components/subscription-expiration-banner/SubscriptionBanners.tsx";
import { MAIN_CONTENT_ID } from "../constants.ts";
import { CommandPalette } from "../header/command-palette/CommandPalette.tsx";
import { Header } from "../header/Header.tsx";
import { RegisteredHeaderWidgets } from "../header/registerProtectHeaderWidget.tsx";
import { MobileSidebar, Sidebar } from "./sidebar/Sidebar.tsx";
import { useShellTranslation } from "../../i18n/i18next.ts";
import { usePageScrollRestoration } from "./usePageScrollRestoration.ts";
import { UserMenu } from "./user-menu/UserMenu.tsx";
import { WorkspaceMenu } from "./workspace/WorkspaceMenu.tsx";

interface AuthenticatedLayoutRef {
    toggleSidebar: () => void;
    openSidebar: () => void;
    closeSidebar: () => void;
}
export interface AuthenticatedLayoutProps {
    /**
     * The main content of the layout
     * @default <Outlet />
     */
    mainContent?: ReactNode;
    accentColor?: string;
    stickyZIndex?: number;
    isFullScreen?: boolean;
    /**
     * Hides the workspace switcher, the user menu and the command palette. For a page that is a
     * single task the user finishes or cancels, where navigating away part-way would strand the work.
     */
    isDistractionFree?: boolean;
    /**
     * Shows a close button at the end of the header and calls this when it is pressed.
     */
    onClose?: () => void;
    isBannersHidden?: boolean;
}

const NavBarHeight = "4.5rem";

export function AuthenticatedLayout({
    mainContent = <Outlet />,
    accentColor,
    stickyZIndex = 10,
    isFullScreen = false,
    isDistractionFree = false,
    onClose,
    isBannersHidden = false
}: AuthenticatedLayoutProps) {
    const { t } = useShellTranslation();
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const isMobile = useResponsiveValue({ base: true, sm: false });
    const ref = useRef<AuthenticatedLayoutRef | null>(null);
    const mainScrollRef = usePageScrollRestoration<HTMLElement>();
    useEffect(() => {
        if (!isMobile && isSidebarOpen) {
            setIsSidebarOpen(false);
        }
    }, [isMobile, isSidebarOpen]);

    useImperativeHandle(
        ref,
        () => ({
            toggleSidebar: () => setIsSidebarOpen(value => !value),
            openSidebar: () => setIsSidebarOpen(true),
            closeSidebar: () => setIsSidebarOpen(false)
        }),
        []
    );

    return (
        <>
            <SkipToMain href={`#${MAIN_CONTENT_ID}`} />
            <Flex direction="column" flex="1 1 0" minHeight={0}>
                <Div position="sticky" top={0} zIndex={stickyZIndex + 1} UNSAFE_height={NavBarHeight}>
                    <Header
                        hamburger={{
                            onClick: () => ref.current?.toggleSidebar()
                        }}
                        workspaceMenu={isDistractionFree ? undefined : <WorkspaceMenu />}
                        userMenu={isDistractionFree ? undefined : <UserMenu />}
                        headerWidget={isFullScreen || isDistractionFree ? undefined : <RegisteredHeaderWidgets />}
                        commandPalette={isFullScreen || isDistractionFree ? undefined : <CommandPalette />}
                        onClose={onClose}
                        accentColor={accentColor}
                    />
                </Div>
                <Flex position="relative" flex="1 1 0" minHeight={0}>
                    {!isFullScreen &&
                        (isMobile ? (
                            <MobileSidebar open={isSidebarOpen} close={() => setIsSidebarOpen(false)} />
                        ) : (
                            <Flex zIndex={stickyZIndex + 1}>
                                <Sidebar />
                            </Flex>
                        ))}
                    <Main
                        ref={mainScrollRef}
                        display="flex"
                        flexDirection="column"
                        id={MAIN_CONTENT_ID}
                        position="relative"
                        overflow="auto"
                        minHeight={0}
                        minWidth={0}
                        width="100%"
                    >
                        {!isBannersHidden && <SubscriptionBanners />}
                        <Flex direction="column" grow={1} minHeight={0}>
                            <Suspense fallback={<PageLoader aria-label={t("Loaders.loadingPage")} />}>
                                {mainContent}
                            </Suspense>
                        </Flex>
                    </Main>
                </Flex>
            </Flex>
        </>
    );
}
